import { AdminApiError } from '@shared/api/client'

export const interfaceMessages = {
	shell: { subtitle: 'Панель управления фитнес-платформой' },
	sections: {
		dashboard: 'Управление фитнес-платформой',
		exercises: 'Управление упражнениями',
		programs: 'Управление программами',
		media: 'Управление медиатекой',
		archive: 'Управление архивом',
	},
	diagnostics: {
		requestId: 'Идентификатор запроса',
		errorCode: 'Код ошибки',
		technicalDetails: 'Технические сведения',
	},
	common: {
		retry: 'Повторите операцию.',
		unknownError: 'Произошла неизвестная ошибка.',
	},
} as const

export type InterfaceErrorDiagnostics = {
	code?: string
	requestId?: string | null
	technicalMessage?: string
}

export type InterfaceErrorPresentation = {
	title: string
	description: string
	action?: string
	code?: string
	requestId?: string | null
	technicalMessage?: string
}

type ErrorCopy = Pick<InterfaceErrorPresentation, 'title' | 'description' | 'action'>

const exactErrorMessages: Record<string, ErrorCopy> = {
	UNAUTHORIZED: { title: 'Требуется авторизация', description: 'Сеанс отсутствует или больше не действителен.', action: 'Войдите повторно и затем повторите операцию.' },
	INVALID_TOKEN: { title: 'Сеанс недействителен', description: 'Сервер отклонил текущий маркер доступа.', action: 'Войдите повторно.' },
	INVALID_CLAIMS: { title: 'Не удалось проверить полномочия', description: 'Данные текущего сеанса не соответствуют требованиям административного API.', action: 'Войдите повторно. Если ошибка сохранится, обратитесь к администратору доступа.' },
	FORBIDDEN: { title: 'Недостаточно прав', description: 'Текущая роль не разрешает эту операцию.', action: 'Выберите доступное действие или запросите необходимую роль.' },
	VERSION_CONFLICT: { title: 'Объект уже изменён', description: 'На сервере сохранена более новая версия объекта.', action: 'Обновите данные, проверьте изменения и повторите операцию с новой версией.' },
	CONFLICT: { title: 'Обнаружен конфликт', description: 'Операция противоречит текущему состоянию объекта.', action: 'Обновите данные и повторите действие после проверки текущего состояния.' },
	IDEMPOTENCY_CONFLICT: { title: 'Команда не совпадает с предыдущей попыткой', description: 'Этот идентификатор повторной попытки уже использован для другого содержимого.', action: 'Закройте диалог, откройте действие заново и повторите команду.' },
	PRECONDITION_REQUIRED: { title: 'Не указана версия объекта', description: 'Для безопасного изменения серверу требуется актуальный ETag.', action: 'Обновите объект и повторите операцию.' },
	INVALID_ETAG: { title: 'Некорректная версия объекта', description: 'Сервер не смог обработать переданный ETag.', action: 'Обновите объект и повторите операцию.' },
	INVALID_IF_MATCH: { title: 'Некорректное условие изменения', description: 'Условие проверки версии объекта имеет неверный формат.', action: 'Обновите объект и повторите операцию.' },
	VALIDATION_ERROR: { title: 'Данные не прошли проверку', description: 'В форме есть значения, которые сервер не может принять.', action: 'Исправьте отмеченные поля и повторите операцию.' },
	VALIDATION_FAILED: { title: 'Данные не прошли проверку', description: 'Объект не соответствует обязательным правилам.', action: 'Откройте результаты проверки, исправьте ошибки и повторите операцию.' },
	PROGRAM_VALIDATION_FAILED: { title: 'Программа не прошла проверку', description: 'Структура программы содержит ошибки или незаполненные обязательные данные.', action: 'Исправьте указанные недели, дни или упражнения и повторите проверку.' },
	WORKOUT_VALIDATION_FAILED: { title: 'Тренировка не прошла проверку', description: 'Состав тренировки не соответствует обязательным правилам.', action: 'Исправьте параметры упражнений и повторите операцию.' },
	INVALID_LIFECYCLE_TRANSITION: { title: 'Переход между статусами недоступен', description: 'Объект нельзя перевести в запрошенный статус из его текущего состояния.', action: 'Обновите объект и выберите допустимое действие для текущего статуса.' },
	ACTIVE_REFERENCE: { title: 'Объект используется', description: 'Удаление или изменение заблокировано активными ссылками.', action: 'Сначала удалите или измените связанные объекты.' },
	RATE_LIMITED: { title: 'Слишком много запросов', description: 'Сервер временно ограничил частоту административных операций.', action: 'Подождите немного и повторите операцию.' },
	DOWNSTREAM_UNAVAILABLE: { title: 'Зависимый сервис недоступен', description: 'Административный API не смог обратиться к одному из доменных сервисов.', action: 'Повторите операцию позже. При повторении ошибки передайте диагностику оператору.' },
	DOWNSTREAM_ERROR: { title: 'Ошибка зависимого сервиса', description: 'Один из доменных сервисов не смог выполнить запрос.', action: 'Повторите операцию. При повторении ошибки передайте диагностику оператору.' },
	AUTHENTICATION_UNAVAILABLE: { title: 'Сервис авторизации недоступен', description: 'Сервер временно не может проверить текущий сеанс.', action: 'Повторите операцию позже.' },
	AUDIT_UNAVAILABLE: { title: 'Журнал аудита недоступен', description: 'Без обязательной записи аудита изменение не выполняется.', action: 'Повторите операцию после восстановления сервиса аудита.' },
	PROCESSING_REJECTED: { title: 'Обработка файла отклонена', description: 'Медиафайл не прошёл проверку или обработку.', action: 'Откройте технические сведения, исправьте файл и загрузите его заново.' },
	EMPTY_RESPONSE: { title: 'Сервер вернул неполный ответ', description: 'Ответ не содержит ожидаемых данных.', action: 'Повторите операцию. При повторении ошибки передайте диагностику оператору.' },
	UNEXPECTED_RESPONSE: { title: 'Получен неожиданный ответ', description: 'Формат ответа сервера не соответствует административному контракту.', action: 'Повторите операцию. При повторении ошибки передайте диагностику оператору.' },
}

const unavailableServices: Record<string, string> = {
	EXERCISE_SERVICE_UNAVAILABLE: 'Сервис упражнений недоступен',
	PROGRAM_SERVICE_UNAVAILABLE: 'Сервис программ недоступен',
	WORKOUT_SERVICE_UNAVAILABLE: 'Сервис тренировок недоступен',
	MEDIA_SERVICE_UNAVAILABLE: 'Сервис медиатеки недоступен',
}

const notFoundTitles: Record<string, string> = {
	EXERCISE_NOT_FOUND: 'Упражнение не найдено',
	PROGRAM_NOT_FOUND: 'Программа не найдена',
	WORKOUT_NOT_FOUND: 'Тренировка не найдена',
	MEDIA_NOT_FOUND: 'Медиафайл не найден',
	NOT_FOUND: 'Объект не найден',
}

const requiredFieldLabels: Record<string, string> = {
	NAME_REQUIRED: 'Название',
	DAYS_REQUIRED: 'Дни программы',
	EXERCISES_REQUIRED: 'Упражнения',
	MUSCLES_REQUIRED_FOR_PUBLICATION: 'Мышечные группы',
	TECHNIQUE_REQUIRED_FOR_PUBLICATION: 'Техника выполнения',
	TRAINING_DAY_REQUIRED: 'Тренировочный день',
	WORKOUT_REQUIRED: 'Тренировка',
	WORK_TARGET_REQUIRED: 'Целевой объект',
	IDEMPOTENCY_KEY_REQUIRED: 'Идентификатор безопасной повторной попытки',
	ACTOR_REQUIRED: 'Идентификатор администратора',
	AUDIT_REQUIRED: 'Данные аудита',
}

const fallbackByStatus = (status: number): ErrorCopy => {
	if (status === 400) return { title: 'Некорректный запрос', description: 'Сервер не смог обработать переданные данные.', action: 'Проверьте введённые значения и повторите операцию.' }
	if (status === 401) return exactErrorMessages.UNAUTHORIZED
	if (status === 403) return exactErrorMessages.FORBIDDEN
	if (status === 404) return { title: 'Объект не найден', description: 'Запрошенный объект отсутствует или больше недоступен.', action: 'Обновите список и выберите существующий объект.' }
	if (status === 409 || status === 412) return exactErrorMessages.VERSION_CONFLICT
	if (status === 422) return exactErrorMessages.VALIDATION_FAILED
	if (status === 429) return exactErrorMessages.RATE_LIMITED
	if (status >= 500) return { title: 'Сервис временно недоступен', description: 'Сервер не смог завершить операцию.', action: 'Повторите операцию позже. При повторении ошибки передайте диагностику оператору.' }
	return { title: 'Операция не выполнена', description: 'Сервер отклонил административную операцию.', action: interfaceMessages.common.retry }
}

const copyForCode = (code: string, status: number): ErrorCopy => {
	if (exactErrorMessages[code]) return exactErrorMessages[code]
	if (unavailableServices[code]) return { title: unavailableServices[code], description: 'Административный API временно не может получить необходимые данные.', action: 'Повторите операцию позже.' }
	if (notFoundTitles[code]) return { title: notFoundTitles[code], description: 'Объект отсутствует или был изменён другим администратором.', action: 'Обновите список и выберите существующий объект.' }
	if (requiredFieldLabels[code] || code.endsWith('_REQUIRED')) return { title: 'Не заполнены обязательные данные', description: requiredFieldLabels[code] ? `Поле «${requiredFieldLabels[code]}» обязательно.` : 'Для операции не переданы обязательные данные.', action: 'Заполните обязательные поля и повторите операцию.' }
	if (code.startsWith('INVALID_') || code.endsWith('_INVALID')) return { title: 'Некорректные данные', description: 'Одно из переданных значений не соответствует административному контракту.', action: 'Проверьте форму, фильтры или ссылочные значения и повторите операцию.' }
	if (code.includes('REFERENCE')) return exactErrorMessages.ACTIVE_REFERENCE
	return fallbackByStatus(status)
}

export const getInterfaceErrorPresentation = (error: unknown, fallbackTitle = 'Не удалось выполнить операцию'): InterfaceErrorPresentation => {
	if (error instanceof AdminApiError) {
		return { ...copyForCode(error.code, error.status), code: error.code, requestId: error.requestId, technicalMessage: error.message }
	}
	if (error instanceof Error) return { title: fallbackTitle, description: interfaceMessages.common.unknownError, action: interfaceMessages.common.retry, technicalMessage: error.message }
	return { title: fallbackTitle, description: interfaceMessages.common.unknownError, action: interfaceMessages.common.retry }
}

export const getInterfaceErrorDescription = (error: unknown, fallbackTitle?: string) => {
	const presentation = getInterfaceErrorPresentation(error, fallbackTitle)
	return [presentation.description, presentation.action].filter(Boolean).join(' ')
}


export const getInterfaceErrorToast = (error: unknown, fallbackTitle?: string) => {
	const presentation = getInterfaceErrorPresentation(error, fallbackTitle)
	const diagnostics: InterfaceErrorDiagnostics | undefined = presentation.code || presentation.requestId || presentation.technicalMessage
		? { code: presentation.code, requestId: presentation.requestId, technicalMessage: presentation.technicalMessage }
		: undefined
	return {
		title: fallbackTitle ?? presentation.title,
		description: [presentation.description, presentation.action].filter(Boolean).join(' '),
		tone: 'error' as const,
		diagnostics,
	}
}
